import React, { useState, useEffect } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import './ApplicationManager.css';

function ApplicationManager({ user, onBack }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [appName, setAppName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'applications'), orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!appName.trim()) return;

    setSaving(true);
    try {
      if (editingApp) {
        await updateDoc(doc(db, 'applications', editingApp.id), {
          name: appName.trim(),
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'applications'), {
          name: appName.trim(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      resetForm();
    } catch (err) {
      // Be paranoid about error logging — Firestore errors sometimes have
      // useful info hidden in fields that console.error swallows.
      console.error('Error saving application (full):', err);
      console.error('  code:', err?.code);
      console.error('  message:', err?.message);
      console.error('  name:', err?.name);
      console.error('  stack:', err?.stack);
      const detail = err?.code || err?.message || err?.name || JSON.stringify(err) || 'Unknown error';
      alert(`Error saving application: ${detail}\n\nCheck console for full details.`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (app) => {
    setEditingApp(app);
    setAppName(app.name);
    setIsFormOpen(true);
  };

  const handleDelete = async (app) => {
    if (!window.confirm(`Delete "${app.name}"? Existing defects will keep this name but it won't appear in the dropdown.`)) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'applications', app.id));
    } catch (err) {
      console.error('Error deleting application:', err);
      alert(`Error deleting application: ${err.code || err.message || 'Unknown error'}.`);
    }
  };

  const resetForm = () => {
    setAppName('');
    setEditingApp(null);
    setIsFormOpen(false);
  };

  return (
    <div className="application-manager">
      <header className="header">
        <div className="header-content">
          <div>
            <h1>Wixon & Cross LLC</h1>
            <h2>Application Management</h2>
          </div>
          <div className="header-user">
            <span className="user-email">{user.email}</span>
            <span className="role-badge role-badge--admin">Admin</span>
            <button className="btn btn-secondary btn-small" onClick={onBack}>
              ← Back to Defects
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="app-manager-toolbar">
          <h3>Application Names ({applications.length})</h3>
          <button className="btn btn-primary" onClick={() => setIsFormOpen(true)}>
            + Add Application
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading applications...</p>
          </div>
        ) : (
          <>
            {applications.length === 0 ? (
              <div className="empty-state">
                <p>No applications defined yet. Click "Add Application" to create one.</p>
              </div>
            ) : (
              <div className="apps-grid">
                {applications.map(app => (
                  <div key={app.id} className="app-card">
                    <div className="app-card-header">
                      <span className="app-icon">📱</span>
                      <h4>{app.name}</h4>
                    </div>
                    <div className="app-card-actions">
                      <button className="btn btn-small btn-edit" onClick={() => handleEdit(app)}>
                        Edit
                      </button>
                      <button className="btn btn-small btn-delete" onClick={() => handleDelete(app)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {isFormOpen && (
          <div className="modal-overlay" onClick={resetForm}>
            <div className="modal modal-small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingApp ? 'Edit Application' : 'Add Application'}</h3>
                <button className="close-btn" onClick={resetForm}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Application Name *</label>
                  <input
                    type="text"
                    value={appName}
                    onChange={e => setAppName(e.target.value)}
                    required
                    placeholder="e.g., YesLims, GoodTest, etc."
                    autoFocus
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingApp ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="app-manager-note">
          <strong>💡 Note:</strong> Application names appear in the dropdown when creating defects.
          If you delete an application, existing defects will keep the old name but it won't be available for new defects.
        </div>
      </main>

      <footer className="footer">
        <p>&copy; 2026 Wixon & Cross LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default ApplicationManager;
